// RFQ inbox — bring in an RFQ, parse it, then one-click resolve. Production
// surface for v1's RFQ-resolution flow (Create Quote / Order / Rush PO /
// Decline / Follow-up / Save for later).
//
// Three stacked sections:
//
//   1. "Bring in an RFQ" — paste body, drop .msg/.pdf/.dxf, click Extract.
//      Calls Brain.extract({task:'rfq_from_email'}) and lifts the parsed
//      ExtractedRfq into RfqPage state so the action bar can read from it.
//
//      Implementation note: the standalone <RfqExtractTest /> smoke-test
//      component (HomePage) does the same Brain.extract call but doesn't
//      expose an onExtracted callback, so we can't hand its parsed result
//      to the action bar. Rather than refactor RfqExtractTest (forbidden
//      by the task brief), we replicate the form here with the SAME extract
//      logic and .msg-unpacking helper. When RfqExtractTest grows an
//      onExtracted prop in a future pass, this form collapses to a thin
//      wrapper around it.
//
//   2. Summary card + action bar — appears once an RFQ has been extracted.
//      Summary shows the canonical fields (customer, parts, due date,
//      summary, raw body — expandable). Action bar mounts RfqActionButtons.
//
//   3. Recent RFQs — last 5 RFQs handled with their resolution. Reads
//      /rfqs from Firebase. Today this is mostly empty (no intake pipeline
//      yet); we surface a stub empty state until intake lands proper.

import { useEffect, useRef, useState, type DragEvent, type ChangeEvent } from 'react';
import { ref, get, query, orderByChild, limitToLast } from 'firebase/database';
import { Brain } from '@/sdk/brain';
import type { BrainError, ExtractedRfq, RfqAttachment } from '@/sdk/brain';
import { db, awaitFirebaseUser } from '@/shared/firebase';
import { expandMsgFile, isMsgFile } from '@/shared/msgParser';
import { uploadRfqAttachment } from '@/shared/drawingStorage';
import { RfqActionButtons } from '@/components/RfqActionButtons';

interface PendingAttachment extends RfqAttachment {
  sizeBytes: number;
  /** Original File handle for non-.msg drops. Used to upload to the RFQ
   *  pool so the URL travels with the eventual Quote.attachedDrawings. */
  file?: File;
  /** Firebase Storage download URL — set after a successful background
   *  upload to the RFQ pool. */
  uploadedUrl?: string;
}

/** Lightweight UUID for the in-flight RFQ. crypto.randomUUID is available
 *  in all evergreen browsers; falls back to a timestamp if missing. */
function newRfqLocalId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `rfq-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

async function fileToAttachment(file: File): Promise<PendingAttachment> {
  const data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result as string;
      const comma = r.indexOf(',');
      resolve(comma >= 0 ? r.slice(comma + 1) : r);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
  return {
    name: file.name,
    mimeType: file.type || 'application/octet-stream',
    data,
    sizeBytes: file.size,
    file,
  };
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

/** Lightweight record we render in the recent-RFQs list. The actual /rfqs
 *  shape is still being designed (intake pipeline TBD) — we read defensively
 *  off whatever fields are present today. */
interface RecentRfqSummary {
  id: string;
  ts: string;
  customerName: string;
  resolution: 'quoted' | 'ordered' | 'declined' | 'following_up' | 'pending';
  resolutionLabel: string;
  summary: string;
}

function resolutionFor(rfq: Record<string, unknown>): {
  kind: RecentRfqSummary['resolution'];
  label: string;
} {
  if (rfq.decline) return { kind: 'declined', label: 'Declined' };
  if (rfq.quoteId) return { kind: 'quoted', label: 'Quoted' };
  if (rfq.orderId) return { kind: 'ordered', label: 'Ordered' };
  if (rfq.followUpDraftId) return { kind: 'following_up', label: 'Following up' };
  return { kind: 'pending', label: 'Pending' };
}

export function RfqPage() {
  // ===== Extract form state — see header note about why this isn't a
  //       direct mount of <RfqExtractTest />. =====
  const [sender, setSender] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [running, setRunning] = useState(false);
  const [extracted, setExtracted] = useState<ExtractedRfq | null>(null);
  const [rawText, setRawText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [rawExpanded, setRawExpanded] = useState(false);
  // rfqLocalId is minted lazily on first non-.msg file drop and held in a
  // ref so successive drops reuse the same pool folder. Cleared on every
  // clearForm() so the next RFQ gets its own pool.
  const rfqLocalIdRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function ensureRfqLocalId(): string {
    if (!rfqLocalIdRef.current) {
      rfqLocalIdRef.current = newRfqLocalId();
    }
    return rfqLocalIdRef.current;
  }

  // ===== Recent RFQs =====
  const [recent, setRecent] = useState<RecentRfqSummary[]>([]);
  const [recentLoading, setRecentLoading] = useState(true);

  async function loadRecent() {
    setRecentLoading(true);
    try {
      await awaitFirebaseUser();
      // Order by ts, take last 5. The /rfqs schema is still being designed;
      // we read whatever's there today (notably the `decline` sub-records
      // we write from RfqActionButtons) and bucket on best-guess fields.
      const snap = await get(query(ref(db, 'rfqs'), orderByChild('ts'), limitToLast(5)));
      if (!snap.exists()) {
        setRecent([]);
        return;
      }
      const out: RecentRfqSummary[] = [];
      snap.forEach((child) => {
        const val = child.val() as Record<string, unknown>;
        const decline = val.decline as Record<string, unknown> | undefined;
        // Decline-only RFQs (the only kind we currently write) hang their
        // metadata under .decline rather than at the top level.
        const ts = (decline?.ts as string) ?? (val.ts as string) ?? '';
        const customerName =
          (decline?.customerName as string) ?? (val.customerName as string) ?? 'Unknown';
        const summary =
          (decline?.rfqSummary as string) ??
          (val.summary as string) ??
          (decline?.reason ? `Declined: ${decline.reason as string}` : '(no summary)');
        const res = resolutionFor(val);
        out.push({
          id: child.key!,
          ts,
          customerName,
          resolution: res.kind,
          resolutionLabel: res.label,
          summary,
        });
        return false;
      });
      // RTDB returns ascending; show newest first.
      out.sort((a, b) => (b.ts ?? '').localeCompare(a.ts ?? ''));
      setRecent(out);
    } catch {
      // Soft-fail — the recent list is informational, not load-bearing.
      setRecent([]);
    } finally {
      setRecentLoading(false);
    }
  }

  useEffect(() => {
    void loadRecent();
  }, []);

  // ===== File / drag handlers =====
  async function handleFiles(files: FileList | File[]) {
    setError(null);
    const arr = Array.from(files);
    const next: PendingAttachment[] = [];
    const notices: string[] = [];
    // Files we'll background-upload to the RFQ pool so the URLs travel
    // with the eventual Quote.attachedDrawings. .msg-unpacked attachments
    // skipped — no File handle. (Re-uploading those would mean reconstructing
    // a Blob from base64 data; not worth it for the smoke flow.)
    const toUpload: { name: string; file: File }[] = [];

    for (const f of arr) {
      if (isMsgFile(f)) {
        try {
          const expanded = await expandMsgFile(f);
          if (expanded.email.sender && !sender) setSender(expanded.email.sender);
          if (expanded.email.senderEmail && !senderEmail) setSenderEmail(expanded.email.senderEmail);
          if (expanded.email.subject && !subject) setSubject(expanded.email.subject);
          if (expanded.email.body && !emailBody) setEmailBody(expanded.email.body);

          for (const att of expanded.attachments) {
            next.push(att);
          }
          notices.push(
            `Unpacked ${f.name}: ${expanded.attachments.length} embedded attachment${expanded.attachments.length === 1 ? '' : 's'}, email headers auto-filled.`,
          );
        } catch (e) {
          setError(`Failed to parse Outlook .msg "${f.name}": ${(e as Error).message}`);
        }
        continue;
      }
      try {
        const att = await fileToAttachment(f);
        next.push(att);
        toUpload.push({ name: att.name, file: f });
      } catch (e) {
        setError(`Failed to read "${f.name}": ${(e as Error).message}`);
      }
    }
    setAttachments((prev) => [...prev, ...next]);
    if (notices.length > 0) setNotice(notices.join(' '));

    // Background uploads — fire-and-forget, patch state on success. Don't
    // block Extract; the AI call uses base64 inline.
    if (toUpload.length > 0) {
      const localId = ensureRfqLocalId();
      for (const { name, file } of toUpload) {
        void uploadRfqAttachment(localId, file)
          .then((res) => {
            setAttachments((prev) =>
              prev.map((a) =>
                a.name === name && !a.uploadedUrl ? { ...a, uploadedUrl: res.url } : a,
              ),
            );
          })
          .catch((e) => {
            // eslint-disable-next-line no-console
            console.warn(`RFQ pool upload failed for "${name}":`, e);
          });
      }
    }
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) void handleFiles(e.dataTransfer.files);
  }

  function onFileInputChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files) {
      void handleFiles(e.target.files);
      e.target.value = '';
    }
  }

  function removeAttachment(idx: number) {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  }

  async function runExtract() {
    if (!emailBody.trim() && attachments.length === 0) {
      setError('Add an email body OR at least one attachment to extract from.');
      return;
    }
    setRunning(true);
    setError(null);
    setNotice(null);
    setExtracted(null);

    try {
      const res = await Brain.extract<ExtractedRfq>({
        task: 'rfq_from_email',
        input: {
          sender,
          senderEmail,
          subject,
          body: emailBody,
          attachments: attachments.map(({ sizeBytes: _sizeBytes, ...rest }) => rest),
        },
      });
      setExtracted(res.parsed);
      setRawText(emailBody);
    } catch (err) {
      const be = err as BrainError;
      setError(`${be.type ?? 'unknown'}: ${be.message ?? String(err)}`);
    } finally {
      setRunning(false);
    }
  }

  function clearForm() {
    setSender('');
    setSenderEmail('');
    setSubject('');
    setEmailBody('');
    setAttachments([]);
    setExtracted(null);
    setRawText('');
    setError(null);
    setNotice(null);
    setRawExpanded(false);
    // Next RFQ gets its own pool — drop the ref so ensureRfqLocalId()
    // mints a fresh UUID on the next drop.
    rfqLocalIdRef.current = null;
  }

  /** Fired by the action bar after any successful resolve. Clear the form
   *  so Justin can paste the next RFQ, and refresh the recent list. */
  function handleActionDone() {
    clearForm();
    void loadRecent();
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-navy">RFQs</h1>
        <p className="text-sm text-text-mute mt-1">
          Bring in an RFQ — paste the email body, drop the .msg / drawings, and let Jemma extract
          the structured fields. Then one-click: quote it, order it, decline with feedback, or ask
          a question.
        </p>
      </div>

      {/* === Section 1: bring in an RFQ === */}
      <div className="bg-paper border border-line rounded p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-navy">Bring in an RFQ</h2>
          {(extracted || emailBody) && (
            <button
              type="button"
              onClick={clearForm}
              className="text-xs text-text-mute hover:text-orange"
            >
              ↻ Clear form
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs text-text-mute mb-1 uppercase tracking-wide">
              From name
            </label>
            <input
              type="text"
              value={sender}
              onChange={(e) => setSender(e.target.value)}
              placeholder="Sarah Lee"
              className="w-full px-2 py-1 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
            />
          </div>
          <div>
            <label className="block text-xs text-text-mute mb-1 uppercase tracking-wide">
              From email
            </label>
            <input
              type="email"
              value={senderEmail}
              onChange={(e) => setSenderEmail(e.target.value)}
              placeholder="sarah@acme.com"
              className="w-full px-2 py-1 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-text-mute mb-1 uppercase tracking-wide">
              Subject
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="RFQ — 50 bracket assemblies"
              className="w-full px-2 py-1 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs text-text-mute mb-1 uppercase tracking-wide">
              Email body
            </label>
            <textarea
              value={emailBody}
              onChange={(e) => setEmailBody(e.target.value)}
              placeholder="Hi Justin, please quote 50, 100, and 250 of part BRK-2245 per the attached drawing..."
              rows={5}
              className="w-full px-2 py-1 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
            />
          </div>
        </div>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`border-2 border-dashed rounded p-5 text-center text-sm transition-colors cursor-pointer ${
            dragOver ? 'border-orange bg-orange/5' : 'border-line-dark text-text-mute hover:border-orange/50'
          }`}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={onFileInputChange}
            className="hidden"
            accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.dxf,.step,.stp,.csv,.txt,.json,.msg"
          />
          📎 Drop PDFs / images / DXFs / Outlook .msg files here — or click to pick
        </div>

        {notice && (
          <div className="mt-3 bg-green/10 border-l-4 border-green text-text px-3 py-2 text-sm">
            ✓ {notice}
          </div>
        )}

        {attachments.length > 0 && (
          <ul className="mt-3 space-y-1 text-sm">
            {attachments.map((a, i) => (
              <li
                key={`${a.name}-${i}`}
                className="flex items-center justify-between bg-bg px-2 py-1 rounded"
              >
                <span className="font-mono text-xs truncate flex-1">
                  {a.name}{' '}
                  <span className="text-text-mute">
                    ({a.mimeType}, {fmtBytes(a.sizeBytes)})
                  </span>
                </span>
                <button
                  onClick={() => removeAttachment(i)}
                  className="text-text-mute hover:text-orange text-xs ml-2"
                  aria-label={`Remove ${a.name}`}
                >
                  ✕
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={runExtract}
            disabled={running}
            className="px-4 py-2 bg-orange text-white text-sm rounded font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
          >
            {running ? 'Extracting…' : extracted ? 'Re-extract' : 'Extract RFQ'}
          </button>
          {extracted && (
            <span className="text-xs text-text-mute">
              ✓ Parsed — see summary + actions below
            </span>
          )}
        </div>

        {error && (
          <div className="mt-3 bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm">
            ⚠ {error}
          </div>
        )}
      </div>

      {/* === Section 2: parsed RFQ summary + action bar === */}
      {extracted && (
        <div className="mb-4">
          <RfqSummaryCard
            rfq={extracted}
            rawText={rawText}
            expanded={rawExpanded}
            onToggleExpanded={() => setRawExpanded((x) => !x)}
          />
          <div className="mt-3">
            <RfqActionButtons
              rfq={extracted}
              rawText={rawText}
              contactEmailOverride={senderEmail || undefined}
              attachedDrawings={attachments
                .map((a) => a.uploadedUrl)
                .filter((u): u is string => !!u)}
              rfqLocalId={rfqLocalIdRef.current ?? undefined}
              onDone={handleActionDone}
            />
          </div>
        </div>
      )}

      {/* === Section 3: recent RFQs === */}
      <div className="bg-paper border border-line rounded p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-navy">Recent RFQs</h2>
          <button
            type="button"
            onClick={() => void loadRecent()}
            disabled={recentLoading}
            className="text-xs text-text-mute hover:text-orange"
          >
            {recentLoading ? 'Loading…' : '↻ Refresh'}
          </button>
        </div>
        {recentLoading && recent.length === 0 && (
          <div className="text-sm text-text-mute italic">Loading…</div>
        )}
        {!recentLoading && recent.length === 0 && (
          <div className="text-sm text-text-mute italic text-center py-6">
            (history coming soon — full RFQ intake pipeline lands in a future milestone)
          </div>
        )}
        {recent.length > 0 && (
          <ul className="divide-y divide-line">
            {recent.map((r) => (
              <li key={r.id} className="py-2 flex items-start gap-3">
                <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${resolutionBadgeClass(r.resolution)}`}>
                  {r.resolutionLabel}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-text truncate">
                    <span className="font-semibold">{r.customerName}</span>
                    <span className="text-text-mute"> — {r.summary}</span>
                  </div>
                  {r.ts && (
                    <div className="text-[11px] text-text-mute">
                      {new Date(r.ts).toLocaleString()}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function resolutionBadgeClass(kind: RecentRfqSummary['resolution']): string {
  switch (kind) {
    case 'quoted':
      return 'bg-orange/10 text-orange';
    case 'ordered':
      return 'bg-green/10 text-green';
    case 'declined':
      return 'bg-amber/10 text-amber';
    case 'following_up':
      return 'bg-blue/10 text-blue';
    case 'pending':
    default:
      return 'bg-bg text-text-mute border border-line-dark';
  }
}

// =====================================================
// Summary card — renders the parsed ExtractedRfq cleanly
// =====================================================

interface RfqSummaryCardProps {
  rfq: ExtractedRfq;
  rawText: string;
  expanded: boolean;
  onToggleExpanded: () => void;
}

function RfqSummaryCard({ rfq, rawText, expanded, onToggleExpanded }: RfqSummaryCardProps) {
  const c = rfq.customer ?? {};
  const parts = rfq.parts ?? [];
  return (
    <div className="bg-paper border border-line rounded p-4">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <div className="text-xs text-text-mute uppercase tracking-wide">Extracted RFQ</div>
          {rfq.summary && (
            <p className="text-sm text-text mt-1">{rfq.summary}</p>
          )}
        </div>
        {rfq.urgency && (
          <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${urgencyBadgeClass(rfq.urgency)}`}>
            {rfq.urgency}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <SummaryField label="Customer">
          {c.companyName || <em className="text-text-mute">—</em>}
        </SummaryField>
        <SummaryField label="Contact">
          {c.contactName ? (
            <span>
              {c.contactName}
              {c.role ? <span className="text-text-mute"> · {c.role}</span> : null}
            </span>
          ) : (
            <em className="text-text-mute">—</em>
          )}
        </SummaryField>
        <SummaryField label="Email">
          {c.contactEmail ? (
            <span className="font-mono text-xs">{c.contactEmail}</span>
          ) : (
            <em className="text-text-mute">—</em>
          )}
        </SummaryField>
        <SummaryField label="Phone">
          {c.contactPhone ? (
            <span className="font-mono text-xs">{c.contactPhone}</span>
          ) : (
            <em className="text-text-mute">—</em>
          )}
        </SummaryField>
        <SummaryField label="Due date">
          {rfq.dueDate || <em className="text-text-mute">—</em>}
        </SummaryField>
        <SummaryField label="RFQ ref">
          {rfq.rfqRef || <em className="text-text-mute">—</em>}
        </SummaryField>
      </div>

      {parts.length > 0 && (
        <div className="mt-3 pt-3 border-t border-line">
          <div className="text-xs font-semibold text-text-mute uppercase tracking-wide mb-2">
            Parts ({parts.length})
          </div>
          <ul className="space-y-1.5 text-sm">
            {parts.map((p, i) => (
              <li key={i} className="bg-bg rounded px-2 py-1.5">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-xs font-semibold text-navy">
                      {p.partNum || p.customerPN || `Part ${i + 1}`}
                    </div>
                    {p.description && (
                      <div className="text-xs text-text-mute truncate" title={p.description}>
                        {p.description}
                      </div>
                    )}
                  </div>
                  {p.qtyBreaks && p.qtyBreaks.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {p.qtyBreaks.map((q, j) => (
                        <span
                          key={`${q}-${j}`}
                          className="text-[10px] bg-paper border border-line-dark rounded-full px-1.5 py-0.5 font-mono text-text-mute"
                        >
                          {q}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {(p.material || p.dimensions || p.finish) && (
                  <div className="text-[11px] text-text-mute mt-1 flex flex-wrap gap-x-2">
                    {p.material && <span>mat: {p.material}</span>}
                    {p.dimensions && <span>dim: {p.dimensions}</span>}
                    {p.finish && <span>finish: {p.finish}</span>}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {rfq.redFlags && rfq.redFlags.length > 0 && (
        <div className="mt-3 pt-3 border-t border-line">
          <div className="text-xs font-semibold text-amber uppercase tracking-wide mb-1">
            🚩 Red flags
          </div>
          <ul className="text-xs text-text space-y-0.5 list-disc list-inside">
            {rfq.redFlags.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </div>
      )}

      {rawText.trim() && (
        <div className="mt-3 pt-3 border-t border-line">
          <button
            type="button"
            onClick={onToggleExpanded}
            className="text-xs text-text-mute hover:text-orange"
          >
            {expanded ? '▼' : '▶'} Raw RFQ text ({rawText.length} chars)
          </button>
          {expanded && (
            <pre className="mt-2 bg-bg p-3 rounded text-xs font-mono whitespace-pre-wrap text-text max-h-64 overflow-y-auto">
              {rawText}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

function urgencyBadgeClass(u: string): string {
  switch (u) {
    case 'urgent':
      return 'bg-amber text-white';
    case 'high':
      return 'bg-amber/15 text-amber';
    case 'normal':
      return 'bg-bg text-text-mute border border-line-dark';
    case 'low':
    default:
      return 'bg-bg text-text-mute border border-line-dark';
  }
}

interface SummaryFieldProps {
  label: string;
  children: React.ReactNode;
}

function SummaryField({ label, children }: SummaryFieldProps) {
  return (
    <div>
      <div className="text-[10px] text-text-mute uppercase tracking-wide">{label}</div>
      <div className="text-sm text-text">{children}</div>
    </div>
  );
}
